/**
 * PB: Gift options — behaviour for snippets/pb-gift-options.liquid.
 *
 * The block cannot use a <form>: both cart sections wrap their contents in the
 * theme's own <form action="/cart">, and the HTML parser drops a nested <form>
 * start tag, so the element never exists in the DOM. Everything here is
 * therefore driven by explicit clicks rather than a submit event.
 *
 * Listeners are delegated on `document` and bound exactly once, because the
 * block lives inside `.quick-cart__items`, which the theme replaces wholesale
 * via innerHTML on every cart change (refreshQuickCart in theme.js). Scripts
 * inserted through innerHTML never execute, so anything bound directly to the
 * block would die on the first refresh.
 *
 * The drawer is refreshed by dispatching `apps:product-added-to-cart`, the
 * theme's own app-integration hook — it triggers refreshQuickCart() and updates
 * the header cart count, with no change to theme JS.
 */
(function () {
  if (window.pbGiftOptionsReady) return;
  window.pbGiftOptionsReady = true;

  var WINDOW_BOX_HANDLE = 'window-medium-gift-box';
  var GIFT_ATTRIBUTE = 'Gift';

  function routeRoot() {
    var shopify = window.Shopify;
    return (shopify && shopify.routes && shopify.routes.root) || '/';
  }

  function showError(block, message) {
    var error = block.querySelector('[data-pb-gift-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  /* "Is this a gift?" — reveals the picker and records the intent on the cart,
     so it means something even if no box is chosen, and so it survives the
     drawer re-rendering itself. */
  document.addEventListener('change', function (event) {
    var toggle = event.target.closest('[data-pb-gift-toggle]');
    if (!toggle) return;

    var block = toggle.closest('[data-pb-gift]');
    var panel = block && block.querySelector('[data-pb-gift-panel]');
    if (panel) panel.hidden = !toggle.checked;
    if (toggle.checked) syncPinnedOffset();

    var attributes = {};
    attributes[GIFT_ATTRIBUTE] = toggle.checked ? 'Yes' : '';

    fetch(routeRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () {
      /* Non-fatal: the tick still reveals the picker, and the box itself
         carries the gift message. */
    });
  });

  /* In the drawer the Add button is pinned above the theme's fixed
     Subtotal/Checkout footer (see custom.css). That footer overlays the bottom
     of the scrolling form and its height changes (order remarks, discounts),
     so measure the overlap rather than hard-coding it. */
  function syncPinnedOffset() {
    var form = document.querySelector('.quick-cart__form');
    var footer = document.querySelector('.quick-cart__footer');
    if (!form || !footer) return;

    var overlap = form.getBoundingClientRect().bottom - footer.getBoundingClientRect().top;
    if (overlap > 0) form.style.setProperty('--pb-gift-pin-offset', Math.round(overlap) + 'px');
  }

  window.addEventListener('resize', syncPinnedOffset, { passive: true });

  /* Picking a box shows its price on the Add button, so the choice visibly
     registers and it's clear the button is the next step. */
  function updateSubmitLabel(block) {
    var button = block.querySelector('[data-pb-gift-submit]');
    var label = block.querySelector('[data-pb-gift-submit-label]');
    if (!button || !label) return;

    var box = block.querySelector('[data-pb-gift-box]:checked');
    var base = button.dataset.label || label.textContent;
    label.textContent = box && box.dataset.price ? base + ' \u00b7 ' + box.dataset.price : base;
  }

  /* The paper insert only fits the window box */
  document.addEventListener('change', function (event) {
    var box = event.target.closest('[data-pb-gift-box]');
    if (!box) return;

    var block = box.closest('[data-pb-gift-form]');
    if (block) updateSubmitLabel(block);
    syncPinnedOffset();
    var group = block && block.querySelector('[data-pb-gift-insert-group]');
    if (!group) return;

    var fitsInsert = box.dataset.handle === WINDOW_BOX_HANDLE;
    group.hidden = !fitsInsert;

    if (!fitsInsert) {
      group.querySelectorAll('[data-pb-gift-insert]').forEach(function (input) {
        input.checked = false;
      });
    }
  });

  /* Add to cart */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-pb-gift-submit]');
    if (!button) return;

    var block = button.closest('[data-pb-gift-form]');
    if (!block) return;

    event.preventDefault();
    showError(block, '');

    var box = block.querySelector('[data-pb-gift-box]:checked');
    if (!box) {
      showError(block, block.dataset.errorNoBox);
      return;
    }

    var items = [{ id: Number(box.value), quantity: 1 }];

    var message = block.querySelector('[data-pb-gift-message]');
    var text = message ? message.value.trim() : '';
    if (text) {
      items[0].properties = {};
      items[0].properties[message.dataset.propertyName] = text;
    }

    // Only send the insert when its group is actually showing, so a stale
    // selection from a previously chosen window box can't slip through.
    var group = block.querySelector('[data-pb-gift-insert-group]');
    var insert = block.querySelector('[data-pb-gift-insert]:checked');
    if (insert && group && !group.hidden) {
      items.push({ id: Number(insert.value), quantity: 1 });
    }

    button.classList.add('loading');
    button.disabled = true;

    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || '');
          return data;
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));

        // The cart page listens on the theme's internal pub/sub rather than the
        // DOM event above, so it can't be refreshed from here.
        if (block.dataset.context === 'page') window.location.reload();
      })
      .catch(function (error) {
        showError(block, error.message || block.dataset.errorGeneric);
      })
      .finally(function () {
        button.classList.remove('loading');
        button.disabled = false;
      });
  });
})();
